import React from "react";

import { ButtonStandard, Text } from "@performyard/py-components";

import ContentModal from "../../../../../components/Modal/Templates/ContentModal";

import type { VideoConferencingErrorType } from "./types";
import { VIDEO_CONFERENCING_ERROR_TYPE } from "./types";

import type { VideoConferencingErrorModalState } from "./hooks";

type VideoConferencingErrorModalProps = {
	modalState: VideoConferencingErrorModalState;
	onRetry: () => void;
	onContinue: () => void;
	onClose: () => void;
};

const getVideoConferencingErrorModalContent = (
	errorType: VideoConferencingErrorType
): {
	title: string;
	message: string;
	actions: {
		left: { text: string; color: "primary" | "green" | "red" };
		right: { text: string; color: "primary" | "green" | "red" };
	};
} => {
	switch (errorType) {
		case VIDEO_CONFERENCING_ERROR_TYPE.CREATE_FAILED:
			return {
				title: "Failed to Add Video Conferencing",
				message:
					"Would you like to retry or continue without adding video conferencing? You can add video conferencing to this meeting later.",
				actions: {
					left: { text: "Retry", color: "primary" },
					right: { text: "Continue Without Adding", color: "red" },
				},
			};

		case VIDEO_CONFERENCING_ERROR_TYPE.UPDATE_FAILED:
			return {
				title: "Video Conferencing Update Failed",
				message: "Would you like to retry updating the video conferencing meeting?",
				actions: {
					left: { text: "Cancel", color: "primary" },
					right: { text: "Retry", color: "green" },
				},
			};

		case VIDEO_CONFERENCING_ERROR_TYPE.DELETE_FAILED:
			return {
				title: "Failed to Remove Video Conferencing",
				message: "Would you like to retry removing the video conferencing meeting?",
				actions: {
					left: { text: "Cancel", color: "primary" },
					right: { text: "Retry", color: "green" },
				},
			};

		default:
			return {
				title: "Video Conferencing Error",
				message: "An error occurred with the video conferencing integration. Would you like to retry?",
				actions: {
					left: { text: "Cancel", color: "primary" },
					right: { text: "Retry", color: "green" },
				},
			};
	}
};

export const VideoConferencingErrorModal = ({
	modalState,
	onRetry,
	onContinue,
	onClose,
}: VideoConferencingErrorModalProps): React.ReactElement | null => {
	if (modalState.type === "none") {
		return null;
	}

	const { title, message, actions } = getVideoConferencingErrorModalContent(modalState.errorType);

	const getActionHandlers = (): {
		leftButton: { text: string; color: "primary" | "green" | "red"; onClick: () => void };
		rightButton: { text: string; color: "primary" | "green" | "red"; onClick: () => void };
	} => {
		switch (modalState.errorType) {
			case VIDEO_CONFERENCING_ERROR_TYPE.CREATE_FAILED:
				return {
					leftButton: { text: actions.left.text, color: actions.left.color, onClick: onRetry },
					rightButton: { text: actions.right.text, color: actions.right.color, onClick: onContinue },
				};

			case VIDEO_CONFERENCING_ERROR_TYPE.UPDATE_FAILED:
			case VIDEO_CONFERENCING_ERROR_TYPE.DELETE_FAILED:
				return {
					leftButton: { text: actions.left.text, color: actions.left.color, onClick: onClose },
					rightButton: { text: actions.right.text, color: actions.right.color, onClick: onRetry },
				};

			default:
				return {
					leftButton: { text: "Cancel", color: "primary", onClick: onClose },
					rightButton: { text: "Retry", color: "green", onClick: onRetry },
				};
		}
	};

	const actionHandlers = getActionHandlers();

	return (
		<ContentModal
			isOpen={true}
			handleClose={modalState.isRetrying ? () => {} : onClose}
			size="large"
			title={
				<Text size="sub-heading" weight="semi-bold">
					{title}
				</Text>
			}
			buttons={[
				<ButtonStandard
					key="left"
					size="large"
					color={actionHandlers.leftButton.color}
					onClick={actionHandlers.leftButton.onClick}
					disabled={modalState.isRetrying}
				>
					{actionHandlers.leftButton.text}
				</ButtonStandard>,
				<ButtonStandard
					key="right"
					size="large"
					color={actionHandlers.rightButton.color}
					onClick={actionHandlers.rightButton.onClick}
					disabled={modalState.isRetrying}
				>
					{actionHandlers.rightButton.text}
				</ButtonStandard>,
			]}
		>
			<div className="text-center">
				<Text size="paragraph-1" weight="regular">
					{message}
				</Text>
			</div>
		</ContentModal>
	);
};
